import { Button } from '@sixam/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@sixam/ui/card';
import { Input } from '@sixam/ui/input';
import { Label } from '@sixam/ui/label';
import { Text } from '@sixam/ui/text';
import { supabase } from '@sixam/supabase/client';
import { router } from 'expo-router';
import * as React from 'react';
import { type TextStyle, View } from 'react-native';
import { authDestination, signUpHref, validatedInternalReturnPath } from '@/lib/auth-return';

const RESEND_CODE_INTERVAL_SECONDS = 30;

const TABULAR_NUMBERS_STYLE: TextStyle = { fontVariant: ['tabular-nums'] };

type VerifyEmailFormProps = Readonly<{
  email: string;
  returnTo?: string | string[];
}>;

export function VerifyEmailForm({ email, returnTo }: VerifyEmailFormProps) {
  const { countdown, restartCountdown } = useCountdown(RESEND_CODE_INTERVAL_SECONDS);
  const [code, setCode] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  async function onSubmit() {
    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const { error: verifyError } = await supabase.auth.verifyOtp({
        email,
        token: code.trim(),
        type: 'signup',
      });

      if (verifyError) {
        setError(verifyError.message);
      } else {
        router.replace(authDestination(returnTo));
      }
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : 'Could not verify your email.');
    } finally {
      setIsSubmitting(false);
    }
  }

  async function onResend() {
    if (countdown > 0) {
      return;
    }

    setError(null);

    try {
      const { error: resendError } = await supabase.auth.resend({
        type: 'signup',
        email,
      });

      if (resendError) {
        setError(resendError.message);
        return;
      }

      restartCountdown();
    } catch (resendError) {
      setError(resendError instanceof Error ? resendError.message : 'Could not resend the code.');
    }
  }

  return (
    <View className="gap-6">
      <Card className="border-border/0 pb-4 shadow-none sm:border-border sm:shadow-sm sm:shadow-black/5">
        <CardHeader>
          <CardTitle className="text-center text-xl sm:text-left">Verify your email</CardTitle>
          <CardDescription className="text-center sm:text-left">
            Enter the verification code sent to {email}
          </CardDescription>
        </CardHeader>
        <CardContent className="gap-6">
          <View className="gap-6">
            <View className="gap-1.5">
              <Label htmlFor="code">Verification code</Label>
              <Input
                id="code"
                autoCapitalize="none"
                value={code}
                onChangeText={setCode}
                returnKeyType="send"
                keyboardType="numeric"
                autoComplete="sms-otp"
                textContentType="oneTimeCode"
                onSubmitEditing={onSubmit}
              />
              <Button variant="link" size="sm" disabled={countdown > 0} onPress={onResend}>
                <Text className="text-center text-xs">
                  Didn&apos;t receive the code? Resend{' '}
                  {countdown > 0 ? (
                    <Text className="text-xs" style={TABULAR_NUMBERS_STYLE}>
                      ({countdown})
                    </Text>
                  ) : null}
                </Text>
              </Button>
              {error ? (
                <Text className="text-sm text-destructive" selectable>
                  {error}
                </Text>
              ) : null}
            </View>
            <View className="gap-3">
              <Button className="w-full" onPress={onSubmit} disabled={isSubmitting}>
                <Text>{isSubmitting ? 'Verifying...' : 'Continue'}</Text>
              </Button>
              <Button
                variant="link"
                className="mx-auto"
                onPress={() => router.replace(signUpHref(validatedInternalReturnPath(returnTo)))}
              >
                <Text>Cancel</Text>
              </Button>
            </View>
          </View>
        </CardContent>
      </Card>
    </View>
  );
}

function useCountdown(seconds = 30) {
  const [countdown, setCountdown] = React.useState(seconds);
  const intervalRef = React.useRef<ReturnType<typeof setInterval> | null>(null);

  const stopCountdown = React.useCallback(() => {
    if (intervalRef.current !== null) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const startInterval = React.useCallback(() => {
    stopCountdown();

    intervalRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          stopCountdown();
          return 0;
        }

        return prev - 1;
      });
    }, 1000);
  }, [stopCountdown]);

  const restartCountdown = React.useCallback(() => {
    setCountdown(seconds);
    startInterval();
  }, [seconds, startInterval]);

  React.useEffect(() => {
    startInterval();

    return stopCountdown;
  }, [startInterval, stopCountdown]);

  return { countdown, restartCountdown };
}
